import React from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { LoginPage } from './components/auth/LoginPage'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import Sidebar from './components/layout/Sidebar'
import TopBar from './components/layout/TopBar'
import MobileNav from './components/layout/MobileNav'
import WpsWizard from './components/wps/WpsWizard'
import WpsLibrary from './components/wps/WpsLibrary'
import ProjectHub from './components/wps/ProjectHub'
import Settings from './components/wps/Settings'
import ProjectCreate from './components/projects/ProjectCreate'
import ProjectNextSteps from './components/projects/ProjectNextSteps'
import FloorSheet from './components/projects/FloorSheet'
import TeamMembersPage from './components/projects/teamMembers/TeamMembersPage'
import PqrDetails from './components/projects/PqrDetails'
import PqrPreviewPage from './components/projects/PqrPreviewPage'
import WpsPreviewPage from './components/projects/WpsPreviewPage'
import WpsDetails from './components/projects/WpsDetails'
import WpsForm from './components/wps/WpsForm'
import WpsBaseMaterial from './components/wps/WpsBaseMaterial'
import WpsBevelDetails from './components/wps/WpsBevelDetails'
import WpsPassDetails from './components/wps/WpsPassDetails'
import WpsPreheatDetails from './components/wps/WpsPreheatDetails'
import WpsWelderDetails from './components/wps/WpsWelderDetails'
import WpsWeldTimes from './components/wps/WpsWeldTimes'
import WpsClampDetails from './components/wps/WpsClampDetails'
import WpsPostWeld from './components/wps/WpsPostWeld'
import WpsReports from './components/wps/WpsReports'
import WeldParameters from './components/projects/WeldParameters'
import PassAnalyticsPage from './components/projects/PassAnalyticsPage'
import WpsWeldDetails from './components/wps/WpsWeldDetails'
import PipeSetLayout from './components/layout/PipeSetLayout'
import PageLoader from './components/ui/PageLoader'
import MechanicalTestsShell from './mechanical-tests/MechanicalTestsShell'
import MechanicalTestsCatchAll from './mechanical-tests/MechanicalTestsCatchAll'
import { AskEvansPage } from './components/ask-evans/AskEvansPage'
import { GlobalAskEvansFab } from './components/ask-evans/GlobalAskEvansFab'
import {
  LazyAnalyticsLayout,
  LazyConsumableIntelligence,
  LazyDataQuality,
  LazyDistributions,
  LazyECA,
  LazyProjectDetail,
  LazyQC,
  LazyReports,
  LazyReviewQueue,
  LazyRiskEvaluator,
  LazyTestResults,
  LazyUpload,
  LazyWeldDetail,
  LazyWeldTestDetail,
  LazyWelds
} from './mechanical-tests/lazyPages'
import { CatalogProvider } from './contexts/CatalogContext'
import { ProjectsProvider } from './contexts/ProjectsContext'
import { LockedFieldHintProvider } from './contexts/LockedFieldHintContext'

const mechanicalRouteRe = /^\/projects\/[^/]+\/mechanical-tests(\/.*)?$/

export default function App() {
  const location = useLocation()
  const isMechanicalRoute = mechanicalRouteRe.test(location.pathname)
  const fullHeightPage = isMechanicalRoute || location.pathname === '/ask-evans'
  const hideTopBar =
    location.pathname === '/projects/new' ||
    (/^\/projects\/[^/]+\/edit$/.test(location.pathname)) ||
    (/^\/projects\/[^/]+\/next(\/.*)?$/.test(location.pathname)) ||
    (/^\/projects\/[^/]+\/team-members(\/.*)?$/.test(location.pathname)) ||
    location.pathname === '/ask-evans' ||
    isMechanicalRoute
  // When the user arrives via PQR Preview navigation, the destination
  // page renders a sticky `PqrReturnBanner` at the top of its content.
  // The default `pt-4` wrapper below would push that banner 16px away
  // from the topbar, making it look disconnected. Drop the padding for
  // PQR-return arrivals only \u2014 detected by `returnTo` pointing at the
  // preview \u2014 so future `?returnTo=` usages from other flows aren't
  // affected.
  const hasPqrWpsReturn = (() => {
    const returnTo = new URLSearchParams(location.search).get('returnTo') || ''
    return returnTo.includes('/pqr/preview') || returnTo.includes('/wps')
  })()

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ProjectsProvider>
              <CatalogProvider>
                <LockedFieldHintProvider>
            <Toaster
              position="top-right"
              closeButton
              richColors={false}
              toastOptions={{
                classNames: {
                  toast: 'app-toast',
                  title: 'app-toast-title',
                  description: 'app-toast-description',
                  icon: 'app-toast-icon',
                  closeButton: 'app-toast-close',
                  success: 'app-toast-success',
                  error: 'app-toast-error'
                }
              }}
            />
            <div className="h-screen flex bg-page-bg overflow-hidden text-text-grey">
              <div className="flex h-full w-full min-w-0">
                <Sidebar />
                <div className="flex-1 flex flex-col min-h-0 min-w-0 lg:ml-[240px]">
                  <MobileNav />
                  <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                    {!hideTopBar && <TopBar />}
                    <main
                      className={
                        fullHeightPage
                          ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                          : 'flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-4 pb-10'
                      }
                    >
                      <div
                        className={
                          fullHeightPage
                            ? 'flex min-h-0 min-w-0 flex-1 flex-col'
                            : hasPqrWpsReturn
                              ? 'min-w-0'
                              : 'pt-4 min-w-0'
                        }
                      >
                      <Routes>
                        <Route path="/" element={<Navigate to="/resources" replace />} />
                <Route path="/wizard" element={<WpsWizard />} />
                <Route path="/library" element={<WpsLibrary />} />
              <Route path="/resources" element={<ProjectHub />} />
              <Route path="/projects/new" element={<ProjectCreate />} />
              <Route path="/projects/:id/edit" element={<ProjectCreate />} />
              <Route path="/projects/:id/next" element={<ProjectNextSteps />} />
              <Route path="/projects/:id/team-members" element={<TeamMembersPage />} />
              <Route path="/projects/:id/mechanical-tests" element={<MechanicalTestsShell />}>
                <Route
                  index
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading mechanical tests…" />}>
                      <LazyProjectDetail />
                    </React.Suspense>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyReports />
                    </React.Suspense>
                  }
                />
                <Route
                  path="review"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyReviewQueue />
                    </React.Suspense>
                  }
                />
                <Route
                  path="welds"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyWelds />
                    </React.Suspense>
                  }
                />
                <Route
                  path="welds/:weldPk"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyWeldDetail />
                    </React.Suspense>
                  }
                />
                <Route
                  path="welds/:weldPk/tests/:testType"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyWeldTestDetail />
                    </React.Suspense>
                  }
                />
                <Route
                  path="tests/:testType"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyTestResults />
                    </React.Suspense>
                  }
                />
                <Route
                  path="upload"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyUpload />
                    </React.Suspense>
                  }
                />
                <Route
                  path="analytics"
                  element={
                    <React.Suspense fallback={<PageLoader label="Loading…" />}>
                      <LazyAnalyticsLayout />
                    </React.Suspense>
                  }
                >
                  <Route index element={<Navigate to="distributions" replace />} />
                  <Route
                    path="distributions"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyDistributions />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="eca"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyECA />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="consumables"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyConsumableIntelligence />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="qc"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyQC />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="risk-evaluator"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyRiskEvaluator />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="risk-evaluator/:evaluationId"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyRiskEvaluator />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="data-quality"
                    element={
                      <React.Suspense fallback={<PageLoader label="Loading…" />}>
                        <LazyDataQuality />
                      </React.Suspense>
                    }
                  />
                </Route>
                <Route path="*" element={<MechanicalTestsCatchAll />} />
              </Route>
              <Route path="/projects/:id/next/:pipeId" element={<PipeSetLayout />}>
                <Route index element={<WpsForm />} />
                <Route path="base" element={<WpsBaseMaterial />} />
                <Route path="bevel" element={<WpsBevelDetails />} />
                <Route path="pass" element={<WpsPassDetails />} />
                <Route path="pass/bevel/:bevelIndex/:passKey" element={<WpsWeldDetails />} />
                <Route path="pass/:passKey" element={<WpsWeldDetails />} />
                <Route path="preheat" element={<WpsPreheatDetails />} />
                <Route path="clamp" element={<WpsClampDetails />} />
                <Route path="welder" element={<WpsWelderDetails />} />
                <Route path="weld-times" element={<WpsWeldTimes />} />
                <Route path="post-weld" element={<WpsPostWeld />} />
                <Route path="cleaning-grinding" element={<WpsPostWeld />} />
                <Route path="reports" element={<WpsReports />} />
                <Route path="floorsheet" element={<FloorSheet />} />
                <Route path="floorsheet/:weldId/parameters" element={<WeldParameters />} />
                <Route
                  path="floorsheet/:weldId/parameters/passes/:passLabel"
                  element={<PassAnalyticsPage />}
                />
                <Route path="pqr" element={<PqrDetails />} />
                <Route path="pqr/preview" element={<PqrPreviewPage />} />
                <Route path="wps/preview" element={<WpsPreviewPage />} />
                <Route path="wps/reports" element={<WpsDetails />} />
              </Route>
              <Route path="/settings" element={<Settings />} />
              <Route path="/ask-evans" element={<AskEvansPage />} />
                      </Routes>
                      </div>
                    </main>
                  </div>
                </div>
              </div>
            </div>
            <GlobalAskEvansFab />
                </LockedFieldHintProvider>
              </CatalogProvider>
            </ProjectsProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
